import React, { useState, useRef, useMemo, useEffect } from 'react';
import { X } from 'lucide-react';
import axios from 'axios';
import CustomDialog from './CustomDialog'; // CustomDialog 임포트
import { useAuth } from '../contexts/AuthContext';
import * as CONST from '../constants';
import styles from './FilterSidebar.module.css';

const FilterSidebar = ({ isOpen, onClose, onApply, selectedFarm, setDialog: setAppDialog, filterSidebarState, setFilterSidebarState }) => {
    const { user } = useAuth();
    const isFarmOwner = user?.mtype1 === CONST.USER_TYPE_FARM_OWNER;

    const initialFilters = useMemo(() => ({
        gene: 'ALL',
        management: [],
        prodRatio: isFarmOwner ? 50 : 30,
        confRatio: isFarmOwner ? 50 : 70,
        country: '01',
        breed: 'HO',
        ocapaDate: '',
        inbreedingCoefficient: '0',
        HighIndex: 'N', Robotic: 'N', ShowType: 'N', MgmtIntense: 'N', etc: 'N'
    }), [isFarmOwner]);

    // 필터 상태 (props에 저장된 값이 있으면 사용, 없으면 초기값 사용)
    const [filters, setFilters] = useState(filterSidebarState?.filters || initialFilters);
    const [typeOptions, setTypeOptions] = useState([]);
    const [dateOptions, setDateOptions] = useState([]);
    const [nationOptions, setNationOptions] = useState([]);
    const [favorites, setFavorites] = useState([]);
    const [activeTabIndex, setActiveTabIndex] = useState(filterSidebarState?.activeTabIndex || 0);
    const [dialog, setDialog] = useState({ isOpen: false });

    // 초기 로딩 완료 여부 (이미 복구된 상태가 아니면 favorites 로딩 후 Tab 0 전환이 필요함)
    const [isInitialized, setIsInitialized] = useState(!!filterSidebarState?.filters);
    const lastFarmId = useRef(selectedFarm?.id);

    // 내부 상태가 변경될 때 상위 상태 업데이트 (App.jsx)
    useEffect(() => {
        if (setFilterSidebarState) {
            setFilterSidebarState({ filters, activeTabIndex });
        }
    }, [filters, activeTabIndex, setFilterSidebarState]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            setFilters(prev => {
                const newMgmt = checked ? [...prev.management, value] : prev.management.filter(v => v !== value);
                return { ...prev, management: newMgmt };
            });
        } else if (name === 'inbreedingCoefficient') {
            setFilters(prev => ({ ...prev, inbreedingCoefficient: value }));
        } else if (name === 'prodRatio') {
            const v = Number(value);
            setFilters(prev => ({ ...prev, prodRatio: v, confRatio: 100 - v }));
        } else if (name === 'confRatio') {
            const v = Number(value);
            setFilters(prev => ({ ...prev, confRatio: v, prodRatio: 100 - v }));
        } else {
            setFilters(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleTabClick = (tabIndex, currentFavorites = favorites) => {
        setActiveTabIndex(tabIndex);
        const fav = currentFavorites[tabIndex];
        let newFilters;
        if (fav) {
            const mgmt = [];
            if (fav.highindex === 'Y') mgmt.push('01');
            if (fav.robotic === 'Y') mgmt.push('02');
            if (fav.showtype === 'Y') mgmt.push('03');
            if (fav.mgmtintense === 'Y') mgmt.push('04');
            newFilters = {
                ...initialFilters,
                prodRatio: fav.prod_rate || initialFilters.prodRatio,
                confRatio: fav.body_rate || initialFilters.confRatio,
                gene: fav.gen_factor || 'ALL',
                ocapaDate: fav.ocapa_date || (dateOptions.length > 0 ? dateOptions[0].ocapa_date : ''),
                management: mgmt,
                HighIndex: fav.highindex || 'N', Robotic: fav.robotic || 'N', ShowType: fav.showtype || 'N', MgmtIntense: fav.mgmtintense || 'N', etc: fav.etc || 'N',
                inbreedingCoefficient: (() => {
                    const v = parseFloat(fav.inbreed_coeff || '6.25');
                    return ['0', '3.125', '6.25', '12.5'].find(opt => parseFloat(opt) === v) || '6.25';
                })(),
                country: fav.nation || '01', breed: fav.varieties || 'HO'
            };
        } else {
            newFilters = initialFilters;
        }
        setFilters(newFilters);
        onApply(newFilters, typeOptions, false);
    };

    const handleApply = () => {
        onApply(filters, typeOptions);
        onClose();
    };

    const handleSave = async () => {
        if (!selectedFarm?.id) return;
        const fd = new FormData();
        fd.append('action', 'insert'); fd.append('member_id', selectedFarm.id);
        fd.append('seq', activeTabIndex + 1); fd.append('gen_factor', filters.gene);
        fd.append('inbreed_coeff', filters.inbreedingCoefficient); fd.append('ocapa_date', filters.ocapaDate);
        fd.append('nation', filters.country); fd.append('varieties', filters.breed);
        fd.append('HighIndex', filters.management.includes('01') ? 'Y' : 'N');
        fd.append('Robotic', filters.management.includes('02') ? 'Y' : 'N');
        fd.append('ShowType', filters.management.includes('03') ? 'Y' : 'N');
        fd.append('MgmtIntense', filters.management.includes('04') ? 'Y' : 'N');
        try {
            const resp = await axios.post('/api/favorite', fd);
            if (resp.data?.status === 'success') {
                setDialog({ isOpen: true, title: "저장 완료", message: "즐겨찾기에 저장되었습니다.", onClose: () => { setDialog({ isOpen: false }); fetchFavorites(true); } });
            }
        } catch (e) { console.error(e); }
    };

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const resp = await axios.get('/api/code');
                if (resp.data?.typeOptions) setTypeOptions(resp.data.typeOptions);
                if (resp.data?.dateOptions) {
                    const dates = resp.data.dateOptions;
                    setDateOptions(dates);
                    if (dates.length > 0 && !isInitialized && !filters.ocapaDate) {
                        setFilters(prev => ({ ...prev, ocapaDate: dates[0].ocapa_date }));
                    }
                }
                if (resp.data?.nationOptions) setNationOptions(resp.data.nationOptions);
            } catch (e) { console.error(e); }
        };
        fetchOptions();
    }, [isInitialized, filters.ocapaDate]);

    useEffect(() => {
        if (selectedFarm?.id) {
            // 농장이 실제로 변경된 경우에만 초기화 상태를 해제하여 새로 favorites를 로드하고 탭을 리셋함
            if (lastFarmId.current !== selectedFarm.id) {
                setIsInitialized(false);
                lastFarmId.current = selectedFarm.id;
            }
            fetchFavorites();
        }
    }, [selectedFarm?.id]);

    const fetchFavorites = async (forceUpdate = false) => {
        if (!selectedFarm?.id) {
            if (!isInitialized) { setFavorites([]); setFilters(initialFilters); setActiveTabIndex(0); }
            return;
        }
        try {
            const resp = await axios.get('/api/favorite', { params: { member_id: selectedFarm.id } });
            const sorted = (resp.data?.list || []).sort((a, b) => a.seq - b.seq);
            setFavorites(sorted);

            // 중요: 이미 초기화(복구)된 상태라면 탭 전환 로직을 건너뜀
            if (!isInitialized || forceUpdate) {
                if (!forceUpdate) setActiveTabIndex(0);
                handleTabClick(forceUpdate ? activeTabIndex : 0, sorted);
                setIsInitialized(true);
            }
        } catch (e) {
            console.error(e);
            if (!isInitialized) handleTabClick(0, []);
            setIsInitialized(true);
        }
    };

    return (
        <>
            {isOpen && <div className={styles['filter-overlay']} onClick={onClose}></div>}

            {/* 사이드바 본체 */}
            <div className={`${styles['filter-sidebar']} ${isOpen ? styles['open'] : ''}`}>
                <div className={styles['filter-header']}>
                    <h3 className="dialog-title" style={{ margin: 0, padding: 0, border: 'none' }}>교배조건</h3>
                    <button onClick={onClose} className={styles['sidebar-close-button']} style={{ display: 'block' }}>
                        <X size={24} />
                    </button>
                </div>

                <div style={{ padding: '0 1.5rem', borderBottom: '1px solid #e5e7eb' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', whiteSpace: 'nowrap' }}>
                        {[0, 1, 2].map(index => (
                            <button
                                key={index}
                                className={`${styles['toggle-button']} ${styles['tab-button']} ${activeTabIndex === index ? styles['active'] : ''}`}
                                onClick={() => handleTabClick(index)}
                            >
                                {`${index + 1}번`}
                            </button>
                        ))}
                    </div>
                </div>
                <div className={styles['filter-content']} style={{ paddingTop: '1rem' }}>
                    {!isFarmOwner && (
                        <div className={styles['filter-section']}>
                            <label className={styles['form-label']}>생산체형 비율</label>
                            <div className={styles['filter-group']}>
                                <div className={styles['filter-sub-group']}>
                                    <span>생산</span>
                                    <input
                                        type="number"
                                        name="prodRatio"
                                        value={filters.prodRatio}
                                        onChange={handleChange}
                                        className="form-input"
                                        style={{ width: '70px' }}
                                        min="0"
                                        max="100"
                                        step="10"
                                    />
                                </div>
                                <div className={styles['filter-sub-group']}>
                                    <span>체형</span>
                                    <input
                                        type="number"
                                        name="confRatio"
                                        value={filters.confRatio}
                                        onChange={handleChange}
                                        className="form-input"
                                        style={{ width: '70px' }}
                                        min="0"
                                        max="100"
                                        step="10"
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    <div className={styles['filter-section']}>
                        <label className={styles['form-label']}>유전인자</label>
                        <select name="gene" value={filters.gene} onChange={handleChange} className="form-input" style={{ width: '100%' }}>
                            <option value="ALL">ALL</option><option value="A2A2">A2A2</option></select>
                    </div>
                    <div className={styles['filter-section']}>
                        <label className={styles['form-label']}>경영방향</label>
                        <div className={styles['checkbox-group']}>
                            {typeOptions.map(option => (
                                <label key={option.code} className={styles['checkbox-label']}>
                                    <input
                                        type="checkbox"
                                        name="management"
                                        value={option.code}
                                        checked={filters.management.includes(option.code)}
                                        onChange={handleChange}
                                        className={styles['form-checkbox']}
                                    />
                                    {option.value}
                                </label>
                            ))}
                        </div>
                    </div>
                    <div className={styles['filter-section']}>
                        <label className={styles['form-label']}>임계근친계수</label>
                        <div className={styles['button-group']}>
                            {['0', '3.125', '6.25', '12.5'].map(val => (
                                <button
                                    key={val}
                                    name="inbreedingCoefficient"
                                    value={val}
                                    onClick={handleChange}
                                    className={`${styles['toggle-button']} ${filters.inbreedingCoefficient === val ? styles['active'] : ''}`}>
                                    {val}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className={styles['filter-section']}>
                        <label className={styles['form-label']}>선택 국가</label>
                        <select name="country" value={filters.country} onChange={handleChange} className="form-input" style={{ width: '100%' }}>
                            {nationOptions.map(option => (
                                <option key={option.code} value={option.code}>{option.cname}</option>
                            ))}
                        </select>
                    </div>
                    <div className={styles['filter-section']}>
                        <label className={styles['form-label']}>품종</label>
                        <select name="breed" value={filters.breed} onChange={handleChange} className="form-input" style={{ width: '100%' }}>
                            <option value="HO">HO</option><option value="JE">JE</option><option value="GE">GE</option></select>
                    </div>
                    <div className={styles['filter-section']}>
                        <label className={styles['form-label']}>성적 기준일</label>
                        <select
                            name="ocapaDate"
                            value={filters.ocapaDate}
                            onChange={handleChange}
                            className="form-input"
                            style={{ width: '100%' }}
                        >
                            {dateOptions.map(option => (
                                <option key={option.ocapa_date} value={option.ocapa_date}>{option.ocapa_date}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className={styles['filter-footer']}>
                    <button onClick={() => setFilters(initialFilters)} className="secondary-button" style={{ flex: 1 }}>초기화</button>
                    {isFarmOwner && selectedFarm?.gubun !== '3' && <button onClick={handleSave} className="action-button button-green" style={{ flex: 1 }}>저장</button>}
                    <button onClick={handleApply} className="action-button" style={{ flex: 1 }}>찾기</button>
                </div>
            </div>

            {/* 사이드바 전용 다이얼로그 */}
            <div onClick={e => e.stopPropagation()}>
                <CustomDialog
                    isOpen={dialog.isOpen}
                    title={dialog.title}
                    message={dialog.message}
                    onClose={dialog.onClose}
                />
            </div>
        </>
    );
};

export default FilterSidebar;
